import React from 'react';
import { View } from 'react-native';
import Toast from 'react-native-root-toast';
import moment from 'moment';

import { isStockDeliveryItemDelivered } from '../../../common/utils/warehousing/stockDeliveryStatus';

import { useThemedStyles } from '../../hooks/useThemedStyles';
import {
	SecondaryButton,
	StatusBadge,
	Tag,
} from '../../design/components';
import Text from '../../components/text/Text';

import { getExpirySummaryText } from './StockDeliveryDetailHelpers';

const StockDeliveryItemCard = ({
	item,
	isScannedMatch,
	timezone,
	canModifyStockDeliveries,
	isUpdatingDeliveredAt,
	isUnreceivingItem,
	onLayout,
	onToggleDelivered,
	onUnstock,
	onOpenReceive,
	onViewBatches,
}) => {
	const styles = useThemedStyles(createStyles);
	const isDelivered = isStockDeliveryItemDelivered(item);
	const isStocked = Boolean(item.receivedAt);
	const deliveredAtLabel = isDelivered
		? moment(item.deliveredAt || item.receivedAt).format('DD MMM YYYY HH:mm')
		: 'Not delivered';
	const stockedAtLabel = isStocked
		? moment(item.receivedAt).format('DD MMM YYYY HH:mm')
		: 'Not stocked';
	const quantityInUnits = Math.round(Number(item.quantity) || 0);
	const ownerTimezone = item.expiryPolicyContext?.timezone || timezone;
	const expirySummary = getExpirySummaryText(item.expiryBatches, ownerTimezone);
	const expiryCapturedAtLabel = item.expiryCapturedAt ? moment(item.expiryCapturedAt).format('DD MMM YYYY HH:mm') : 'Not captured';
	const batchCount = (item.expiryBatches || []).length;
	const itemStatusText = isStocked
		? 'Delivered & stocked'
		: (isDelivered ? 'Delivered' : 'Pending delivery');

	return (
		<View
			onLayout={(event) => onLayout(item.id, event)}
			style={[
				styles.itemCard,
				isScannedMatch && styles.itemCardHighlighted,
			]}
		>
			<View style={styles.itemHeader}>
				<Text style={styles.itemTitle}>{item.product?.name || 'Unknown product'}</Text>
				<StatusBadge
					size="small"
					label={itemStatusText}
					tone={isStocked ? 'success' : (isDelivered ? 'primary' : 'neutral')}
				/>
			</View>
			{isScannedMatch && (
				<Tag label="Scanned match" tone="primary" style={styles.scannedMatch} />
			)}
			<Text style={styles.itemMeta}>
					Quantity: {quantityInUnits} units
			</Text>
			<Text style={styles.itemMeta}>
					Delivered at: {deliveredAtLabel}
			</Text>
			<Text style={styles.itemMeta}>
					Stocked at: {stockedAtLabel}
			</Text>
			<Text style={styles.itemMeta}>
					Expiry summary: {expirySummary}
			</Text>
			<Text style={styles.itemMeta}>
					Expiry captured at: {expiryCapturedAtLabel}
			</Text>
			<View style={styles.itemAction}>
				<SecondaryButton
					style={styles.itemActionButton}
					label={isDelivered ? 'Undo delivered' : 'Mark delivered'}
					onPress={() => onToggleDelivered(item)}
					loading={isUpdatingDeliveredAt}
					disabled={!canModifyStockDeliveries || !item.id || isStocked}
				/>
				<SecondaryButton
					style={styles.itemActionButton}
					label={isStocked ? 'Unstock item' : 'Stock item with batches'}
					onPress={() => {
						if (!item.id) {
							return;
						}

						if (isStocked) {
							onUnstock(item.id);
							return;
						}

						if (!isDelivered) {
							Toast.show('Mark item as delivered before stocking it.');
							return;
						}

						onOpenReceive(item);
					}}
					loading={isUnreceivingItem && isStocked}
					disabled={!canModifyStockDeliveries || !item.id}
				/>
				{isStocked && (
					<SecondaryButton
						style={styles.itemActionButton}
						variant="tonal"
						label={batchCount ? `View batches (${batchCount})` : 'View batches'}
						onPress={() => onViewBatches(item.id)}
					/>
				)}
			</View>
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space, radius } }) => ({
	scannedMatch: {
		alignSelf: 'flex-start',
		marginTop: space.xs,
	},
	itemCard: {
		backgroundColor: colors.raised,
		borderRadius: radius.card,
		paddingHorizontal: 14,
		paddingVertical: space.md,
		marginBottom: space.sm,
		borderWidth: 1,
		borderColor: colors.raisedBorder,
		gap: space.xs,
	},
	itemCardHighlighted: {
		backgroundColor: colors.current,
		borderColor: colors.currentBorder,
		borderWidth: 2,
	},
	itemHeader: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: space.md,
	},
	itemTitle: {
		...type.rowTitleSemibold,
		flex: 1,
		color: colors.text,
	},
	itemMeta: {
		...type.secondary,
		color: colors.textSecondary,
	},
	itemAction: {
		marginTop: space.sm,
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: space.sm,
	},
	itemActionButton: {
		flexGrow: 1,
		flexBasis: 140,
	},
});

export default StockDeliveryItemCard;
